import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowTrendUp } from '@fortawesome/pro-light-svg-icons/faArrowTrendUp';
import { faCircleInfo } from '@fortawesome/pro-light-svg-icons/faCircleInfo';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { TypedMemo } from '../../../common/types';
import { Panel, PanelBody, PanelHead } from '../../components/primitives/Panel';
import { Cover } from '../../components/primitives/Cover';
import { SMLLink } from '../../components/SMLLink/SMLLink';
import { SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { useArtistsTopGaining } from '../../hooks/useArtistsTopGainingHook';
import { BrowseFollowButton } from '../results/BrowseFollowButton';
import { BrowseOpenLink } from '../results/BrowseOpenLink';
import { BrowseTryChips } from './BrowseTryChips';
import { browseCreditMessages, browseStateMessages as messages } from '../browseMessages';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import rowStyles from '../results/BrowseRows.module.scss';
/* eslint-enable css-modules/no-unused-class */
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './BrowseStates.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface BrowseFirstVisitProps {
  onSearch: (query: string) => void;
  onPasteLink: () => void;
  creditsLeft: number | null;
  creditsLimit: number | null;
}

/**
 * Design b1: the page with no query. "Try" chips fill the box, "Moving this week" reuses
 * `artists.topGaining` (the call Trends uses), and a short panel says how the credits work.
 */
const BrowseFirstVisitComponent: React.FC<BrowseFirstVisitProps> = ({
  onSearch,
  onPasteLink,
  creditsLeft,
  creditsLimit,
}) => {
  const intl = useIntl();
  const moving = useArtistsTopGaining({ page: 1, limit: 3 });
  const artists = moving.data?.artists ?? [];

  return (
    <div className={rowStyles.sectionStack}>
      <BrowseTryChips onSearch={onSearch} onPasteLink={onPasteLink} />
      {artists.length > 0 && (
        <Panel as="section">
          <PanelHead
            title={intl.formatMessage(messages.movingTitle)}
            icon={<FontAwesomeIcon icon={faArrowTrendUp} />}
            subtitle={intl.formatMessage(messages.movingSub)}
            right={
              <SMLLink to={SMLRoutes.trends} className={rowStyles.linkButton}>
                {intl.formatMessage(messages.openTrends)}
              </SMLLink>
            }
          />
          <PanelBody flush>
            {artists.map((artist) => (
              <div key={artist.artistMaid} className={clsx(rowStyles.resultRow, rowStyles.compactGrid)}>
                <Cover src={artist.imageUrl} label={artist.mainArtistName} size={40} radius="50%" />
                <BrowseOpenLink kind="artist" id={artist.artistMaid} state="costsCredit" className={rowStyles.nameCell}>
                  <span className={rowStyles.nameMain}>{artist.mainArtistName}</span>
                </BrowseOpenLink>
                <span />
                <span className={rowStyles.right}>
                  <BrowseFollowButton
                    kind="artist"
                    id={artist.artistMaid}
                    name={artist.mainArtistName}
                    followed={false}
                  />
                </span>
              </div>
            ))}
          </PanelBody>
        </Panel>
      )}
      <Panel as="section">
        <PanelHead
          title={intl.formatMessage(messages.howTitle)}
          icon={<FontAwesomeIcon icon={faCircleInfo} />}
          right={
            creditsLeft !== null && creditsLimit !== null
              ? intl.formatMessage(browseCreditMessages.left, { left: creditsLeft, limit: creditsLimit })
              : undefined
          }
        />
        <PanelBody>
          <div className={styles.rules}>
            {[messages.ruleSearchFree, messages.ruleOpenCosts, messages.ruleAgainFree].map((rule) => (
              <p key={rule.id} className={styles.rule}>
                <FontAwesomeIcon icon={faCheck} />
                <span>{intl.formatMessage(rule)}</span>
              </p>
            ))}
          </div>
        </PanelBody>
      </Panel>
    </div>
  );
};

export const BrowseFirstVisit = TypedMemo(BrowseFirstVisitComponent);
